import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LoadingOutlined, LockOutlined } from '@ant-design/icons';
import { useEditorContext } from '../contexts/EditorContext';
// 样式在 styles/index.scss（全局类名）

const HIDDEN = { display: 'none' };

/**
 * 章节改稿进度遮罩：只盖住正在改稿的章节，其他章节可以继续编辑
 * 渲染在纸张容器（position: relative）内部。
 */
export const ChapterProcessingOverlay = () => {
    const {
        editor,
        chapterRewriteInfo,
        chapterRewriting,
        chapterRewriteProgress,
        chapterRewriteStatus,
        interruptChapterRewrite,
    } = useEditorContext();
    const [overlayStyle, setOverlayStyle] = useState(HIDDEN);
    const overlayRef = useRef(null);

    const updateOverlayPosition = useCallback(() => {
        if (!editor || !chapterRewriteInfo || !chapterRewriting) {
            setOverlayStyle(HIDDEN);
            return;
        }
        const paper = overlayRef.current?.parentElement;
        if (!paper) return;

        try {
            const editorDom = editor.view.dom;
            const { blockId } = chapterRewriteInfo;
            const headingElement = blockId
                ? editorDom.querySelector(`[data-block-id="${blockId}"]`)
                : null;
            const headingWrapper = headingElement?.closest('.tiptap-heading-wrapper');
            if (!headingWrapper) {
                // 找不到标题：盖住整篇
                setOverlayStyle({ display: 'flex', top: 0, left: 0, right: 0, bottom: 0 });
                return;
            }

            // 章节结束：下一个同级或更高级标题
            const doc = editor.state.doc;
            let headingNode = null;
            let headingPos = null;
            doc.descendants((node, pos) => {
                if (headingNode) return false;
                if (node.type.name === 'heading' && node.attrs.blockId === blockId) {
                    headingNode = node;
                    headingPos = pos;
                    return false;
                }
                return true;
            });

            let nextBlockId = null;
            if (headingNode) {
                const level = headingNode.attrs.level;
                const headingEnd = headingPos + headingNode.nodeSize;
                doc.nodesBetween(headingEnd, doc.content.size, (node) => {
                    if (nextBlockId) return false;
                    if (node.type.name === 'heading' && node.attrs.level <= level) {
                        nextBlockId = node.attrs.blockId;
                        return false;
                    }
                    return true;
                });
            }

            const paperRect = paper.getBoundingClientRect();
            const wrapperRect = headingWrapper.getBoundingClientRect();
            const top = wrapperRect.top - paperRect.top;

            let bottom = paperRect.bottom;
            if (nextBlockId) {
                const nextElement = editorDom.querySelector(`[data-block-id="${nextBlockId}"]`);
                const nextWrapper = nextElement?.closest('.tiptap-heading-wrapper');
                if (nextWrapper) bottom = nextWrapper.getBoundingClientRect().top;
            } else {
                bottom = editorDom.getBoundingClientRect().bottom;
            }

            setOverlayStyle({
                display: 'flex',
                top: `${Math.max(0, top)}px`,
                height: `${Math.max(80, bottom - wrapperRect.top)}px`,
                left: 0,
                right: 0,
            });
        } catch (error) {
            console.error('计算遮罩位置失败:', error);
        }
    }, [editor, chapterRewriteInfo, chapterRewriting]);

    useEffect(() => {
        if (!(chapterRewriting && chapterRewriteInfo)) {
            setOverlayStyle(HIDDEN);
            return undefined;
        }
        updateOverlayPosition();
        const timer = setInterval(updateOverlayPosition, 200);
        window.addEventListener('resize', updateOverlayPosition);
        return () => {
            clearInterval(timer);
            window.removeEventListener('resize', updateOverlayPosition);
        };
    }, [chapterRewriting, chapterRewriteInfo, updateOverlayPosition]);

    if (!chapterRewriting || !chapterRewriteInfo) return null;

    const messages = [
        '正在分析上下文…',
        '正在检索学术词汇…',
        '正在构建逻辑框架…',
        'AI 正在撰写内容…',
        '正在润色格式…',
    ];
    const fallback = `${messages[Math.min(Math.floor(chapterRewriteProgress / 20), messages.length - 1)]} ${chapterRewriteProgress}%`;

    return (
        <div
            ref={overlayRef}
            className="chapter-processing-overlay show"
            style={overlayStyle}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="lock-hint">
                <LockOutlined />
                本节改稿中，暂时锁定编辑
            </div>
            <div className="chapter-status-bar">
                <LoadingOutlined className="status-spinner" />
                <span className="status-progress-text">{chapterRewriteStatus || fallback}</span>
                <button type="button" className="btn-interrupt" onClick={interruptChapterRewrite}>
                    中断
                </button>
            </div>
        </div>
    );
};

export default ChapterProcessingOverlay;
